import React, {useContext, createContext, Dispatch, useEffect} from "react"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import {useImmerReducer} from "use-immer"
import {UpdateBugets, useBudgetsList, useBudgetsPermissions, useBudgetsUpdate} from "@irati/wallet/hooks/budget"
import {reducer, State, Action, initialState, EditableBudgetCategories, Validation, MAX_BUDGETS} from "./reducer"

export type {EditableBudgetCategories, EditableBudget} from "./reducer"

export interface UseBudgetManagement {
  state: State
  dispatch: Dispatch<Action>
  submit: () => Promise<void>
  validate: (cb: () => any) => void
}

const budgetContext = createContext<UseBudgetManagement | null>(null)

export function BudgetManagementProvider({children}: {children: React.ReactNode}) {
  const data = useBudgetManagementData()
  return <budgetContext.Provider value={data}>{children}</budgetContext.Provider>
}

/**
 * useBudgetManagement is intended for use in the wallet estate to allow admins/managers
 * to update their budget categories for their reward wallet
 *
 * for a simple readonly view of the budget categories, see `useBudgetsList`.
 */
export function useBudgetManagement() {
  const context = useContext(budgetContext)
  if (!context) throw new Error("useBudgetManagementContext must be used inside of a BudgetManagementProvider")

  return context
}

function useBudgetManagementData(): UseBudgetManagement {
  const {data} = useBudgetsList()
  const {canManageCategories} = useBudgetsPermissions()
  const [state, dispatch] = useImmerReducer(reducer, initialState)

  const {update} = useBudgetsUpdate()

  const {dirty, formErrors, submitting} = state

  const validate: UseBudgetManagement["validate"] = (cb) => {
    const {budgets} = state
    if (!budgets) return
    const errors = validateForm(budgets)

    if (errors.length > 0) {
      dispatch({type: "formErrors", data: errors})
    } else {
      cb()
    }
  }

  const submit = async () => {
    const {budgets} = state
    if (!budgets || submitting || formErrors || !dirty) return

    validate(async () => {
      dispatch({type: "startMutation"})
      const updatedBudgets: UpdateBugets = {
        categories: budgets.categories.map(({metadata, tag, ...cat}) => ({...cat, color: metadata.color, tagId: tag.uuid})),
        newCategories: budgets.newCategories.map(({metadata, tag, ...cat}) => ({...cat, color: metadata.color, tagId: tag?.uuid!}))
      }
      try {
        await update(updatedBudgets)
        tracking.confirmBudgetChange(getTrackingDeltas(budgets))
        dispatch({type: "endMutation", data: {}})
        dispatch({type: "confirmCancel"})
      } catch (e: any) {
        dispatch({type: "endMutation", data: {error: e}})
      }
    })
  }

  useEffect(() => {
    if (data) {
      dispatch({type: "budgetDataUpdated", data: {budgets: data, canManageCategories}})
    }
  }, [data, dispatch])

  return {
    state,
    dispatch,
    submit,
    validate
  }
}

/**
 * While many form field validate themselves, some things can only be validated against the entire form
 */
function validateForm(budgets: EditableBudgetCategories): Validation[] {
  const errors: Validation[] = []
  const {categories, newCategories} = budgets

  const totalLength = categories.length + newCategories.length

  if (totalLength > MAX_BUDGETS) {
    errors.push({id: "wallet.reward.budget.dashboard.validation.tooManyBudgets"})
  }

  const cats = categories.map(({name, uuid, metadata}) => ({name, uuid, metadata})).concat(newCategories)

  const duplicateColors = getDuplicates(cats.map((c) => c.metadata?.color))
  const duplicateNames = getDuplicates(cats.map((c) => c.name.toLowerCase().replaceAll(/ /g, "")))

  if (duplicateColors.length > 0) {
    errors.push({id: "wallet.reward.budget.dashboard.validation.duplicateColors", values: {type: "colors", names: duplicateColors}})
  }

  if (duplicateNames.length > 0) {
    errors.push({id: "wallet.reward.budget.dashboard.validation.duplicateNames", values: {type: "name", names: duplicateNames}})
  }

  return errors
}

function getDuplicates(ls: string[]) {
  const duplicates = ls.filter((item, index) => ls.indexOf(item) !== index)
  return Array.from(new Set(duplicates))
}

function getTrackingDeltas({categories, newCategories}: EditableBudgetCategories) {
  const updateDeltas = categories.reduce(
    (deltas, curr) => {
      if (curr.changes?.tag) deltas.tag_updated++
      if (curr.changes?.name) deltas.name_updated++
      if (curr.changes?.amount) deltas.budget_updated++
      return deltas
    },
    {name_updated: 0, budget_updated: 0, tag_updated: 0}
  )

  return {
    ...updateDeltas,
    new_categories: newCategories.length
  }
}
